import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';

export const PLAN_LABELS = {
  free: 'Free Trial', essential: 'Essential', pro_intelligence: 'Pro-Intelligence', enterprise: 'Enterprise',
};

// Inline locked-feature panel (used for gated routes).
export const LockedFeaturePanel = ({ capability, requiredPlan, title }) => {
  const navigate = useNavigate();
  return (
    <div className="max-w-md mx-auto mt-24 p-8 rounded-3xl bg-white/60 backdrop-blur-xl border border-white/70 shadow-xl text-center" data-testid="plan-locked-panel">
      <Lock className="mx-auto mb-3 text-amber-500" size={34} />
      <h2 className="text-lg font-semibold text-stone-900">{title || 'Upgrade to unlock this feature'}</h2>
      <p className="text-sm text-stone-500 mt-1">
        {requiredPlan ? `Available on ${PLAN_LABELS[requiredPlan] || requiredPlan} and above.` : 'Not included in your current plan.'}
      </p>
      <button
        type="button"
        onClick={() => navigate(`/billing?upgrade=${capability || ''}`)}
        className="mt-5 inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700 transition-colors"
        data-testid="plan-locked-upgrade-btn"
      >
        <Sparkles size={14} />View plans
      </button>
    </div>
  );
};

// Global modal: opened by the axios interceptor on any 402 `plan_required` response.
export const PlanUpgradeModal = () => {
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const h = (e) => setDetail(e.detail || {});
    window.addEventListener('bc:plan-required', h);
    return () => window.removeEventListener('bc:plan-required', h);
  }, []);
  if (!detail) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4" style={{ pointerEvents: 'auto' }} data-testid="plan-upgrade-modal">
      <div className="max-w-md w-full rounded-3xl bg-white/85 backdrop-blur-xl border border-white/70 shadow-2xl p-6">
        <div className="flex items-center gap-2 text-amber-600"><Lock size={18} /><span className="text-xs font-semibold uppercase tracking-widest">Upgrade required</span></div>
        <h3 className="mt-2 text-lg font-semibold text-stone-900">{detail.message || 'This feature is not included in your plan.'}</h3>
        {detail.required_plan && (
          <p className="text-sm text-stone-500 mt-1">Unlock with <span className="font-semibold text-stone-800">{PLAN_LABELS[detail.required_plan]}</span>.</p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setDetail(null)} className="px-4 py-1.5 text-sm text-stone-500 hover:text-stone-900" data-testid="plan-upgrade-close">Not now</button>
          <button type="button" onClick={() => { setDetail(null); navigate(`/billing?upgrade=${detail.capability || ''}`); }} className="px-5 py-1.5 rounded-full bg-stone-900 text-white text-sm font-semibold hover:bg-stone-700" data-testid="plan-upgrade-go">View plans</button>
        </div>
      </div>
    </div>
  );
};

// Fire the global modal from UI-level locks (e.g. a gated button).
export const promptUpgrade = (capability, requiredPlan, message) =>
  window.dispatchEvent(new CustomEvent('bc:plan-required', { detail: { capability, required_plan: requiredPlan, message } }));
